import type { AxiosResponseHeaders, RawAxiosResponseHeaders } from "axios"

type HeaderSource = AxiosResponseHeaders | RawAxiosResponseHeaders | Record<string, unknown>

function headerValue(
  headers: HeaderSource,
  name: string
): string | undefined {
  const lower = name.toLowerCase()
  if (typeof (headers as AxiosResponseHeaders).get === "function") {
    const value = (headers as AxiosResponseHeaders).get(name)
    if (value != null) return String(value)
    const alt = (headers as AxiosResponseHeaders).get(lower)
    return alt != null ? String(alt) : undefined
  }
  const record = headers as Record<string, unknown>
  const direct = record[name] ?? record[lower]
  if (direct == null) return undefined
  return Array.isArray(direct) ? String(direct[0]) : String(direct)
}

/** Parse Retry-After from a 429 response (seconds or HTTP-date). */
export function getRetryAfterSeconds(headers: HeaderSource): number | null {
  const raw = headerValue(headers, "Retry-After")
  if (!raw) return null

  const asNumber = Number.parseInt(raw, 10)
  if (Number.isFinite(asNumber) && asNumber >= 0) {
    return asNumber
  }

  const dateMs = Date.parse(raw)
  if (!Number.isNaN(dateMs)) {
    return Math.max(0, Math.ceil((dateMs - Date.now()) / 1000))
  }

  return null
}

export function formatRateLimitMessage(retryAfterSeconds: number | null): string {
  if (retryAfterSeconds != null && retryAfterSeconds > 0) {
    if (retryAfterSeconds >= 60) {
      const minutes = Math.ceil(retryAfterSeconds / 60)
      return `Too many requests. Please try again in ${minutes} minute${minutes === 1 ? "" : "s"}.`
    }
    return `Too many requests. Please try again in ${retryAfterSeconds} second${retryAfterSeconds === 1 ? "" : "s"}.`
  }
  return "Too many requests. Please try again later."
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
